// Schema.org JSON-LD builders for detail + person pages. These power Google
// rich results and make pages citable by AI answer engines. All fields are
// optional-safe — missing data is simply omitted.
import type { MediaDetail, PersonDetail } from "@/types/media";
import { SITE_NAME, SITE_ORIGIN, SITE_TAGLINE } from "./seo";

function dropEmpty<T extends Record<string, unknown>>(obj: T): T {
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
      delete obj[k];
    }
  }
  return obj;
}

// No AggregateRating on any page. The block used to carry the Balasaur Score
// with a count of the sources it blends, which is a rating aggregated from
// other sites. Google's review snippet policy excludes exactly that: ratings
// must come from the site's own users, not be compiled from third parties.
// Marking it up risks a manual action against every title page, and it was
// never what earned a click. The score is visible on the page, which is where
// it has to be believed.

/** Home → Title trail for detail pages.
 *
 *  There used to be a "Movies" or "TV Shows" step between them, pointing at
 *  /?type=movie or /?type=tv. Those are filtered homepage views, and a
 *  filtered view canonicalises to "/", so every one of 61,673 title pages
 *  handed Google a URL that declares itself a copy of another page. Search
 *  Console filed them under "Alternate page with proper canonical tag", and
 *  a validation there can never pass, because the canonical is correct. A
 *  crumb may only name a page that stands on its own. */
export function breadcrumbJsonLd(d: MediaDetail, url: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: SITE_NAME, item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: d.title, item: url },
    ],
  };
}

/** ItemList markup for hub pages presenting a finite ordered set (the arcade
 *  hub's game grid). Positions follow the given order. */
export function itemListJsonLd(o: {
  name: string;
  url: string;
  items: { name: string; url: string }[];
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: o.name,
    url: o.url,
    itemListElement: o.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

/** Home → Play → Game trail for arcade game pages. */
export function arcadeBreadcrumbJsonLd(gameName: string, url: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: SITE_NAME, item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: "Play", item: `${SITE_ORIGIN}/play` },
      { "@type": "ListItem", position: 3, name: gameName, item: url },
    ],
  };
}

/** Site-level identity for the homepage (no SearchAction — search isn't
 *  URL-addressable yet). */
export function websiteJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    description: `${SITE_TAGLINE}. Discover, track, and rate movies and TV.`,
    publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_ORIGIN },
  };
}

function directors(d: MediaDetail) {
  return d.crew
    .filter((c) => c.role === "Director")
    .map((c) => ({ "@type": "Person", name: c.name }));
}

function actors(d: MediaDetail) {
  return d.cast.slice(0, 8).map((c) => ({ "@type": "Person", name: c.name }));
}

export function movieJsonLd(d: MediaDetail, url: string): Record<string, unknown> {
  return dropEmpty({
    "@context": "https://schema.org",
    "@type": "Movie",
    name: d.title,
    url,
    image: d.posterUrl || undefined,
    description: d.overview || undefined,
    datePublished: d.releaseDate || undefined,
    genre: d.genres,
    director: directors(d),
    actor: actors(d),
  });
}

export function tvJsonLd(d: MediaDetail, url: string): Record<string, unknown> {
  return dropEmpty({
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name: d.title,
    url,
    image: d.posterUrl || undefined,
    description: d.overview || undefined,
    startDate: d.releaseDate || undefined,
    numberOfSeasons: d.numberOfSeasons || undefined,
    genre: d.genres,
    actor: actors(d),
  });
}

export function personJsonLd(d: PersonDetail, url: string): Record<string, unknown> {
  return dropEmpty({
    "@context": "https://schema.org",
    "@type": "Person",
    name: d.name,
    url,
    image: d.profileUrl || undefined,
    description: d.biography || undefined,
    birthDate: d.birthday || undefined,
    deathDate: d.deathday || undefined,
    birthPlace: d.placeOfBirth || undefined,
    jobTitle: d.knownForDepartment || undefined,
    sameAs: d.imdbId ? `https://www.imdb.com/name/${d.imdbId}/` : undefined,
    mainEntityOfPage: SITE_ORIGIN,
  });
}
